import { getOrderQuery } from '@/api/orders/orderQueries';
import { Button } from '@/components/common/Button';
import type { OrderItem } from '@/domain/Order';
import { useQuery } from '@tanstack/react-query';

interface SupplementData {
  item: OrderItem;
  quantity: number;
  type: string;
}

interface SupplementFactsPreviewOldProps {
  orderId: number;
  name: string;
  supplements: Array<SupplementData>;
  instruction: string;
  onBack: () => void;
}

/** Legacy label preview (pre Lifetelos A4 redesign). */
export function SupplementFactsPreviewOld({
  orderId,
  name,
  supplements,
  instruction,
  onBack,
}: SupplementFactsPreviewOldProps) {
  const { data: orderResponse } = useQuery(getOrderQuery(orderId));
  const orderItems = orderResponse?.data.items || [];
  const orderLotNumber = orderResponse?.data.lot_number;

  const selectedCount = supplements.length;
  const totalPackets = 30;

  const getItemDescription = (itemName?: string): string => {
    const apiItem = orderItems.find((item) => item.name === itemName);
    if (apiItem?.description) {
      return apiItem.description;
    }
    return '';
  };

  return (
    <div
      className="h-[75vh] w-[550px] space-y-0 overflow-y-auto bg-white p-3 text-black"
      style={{ fontFamily: "'Flama', sans-serif" }}
    >
      {name && <h2 className="text-[50px] leading-[75px] font-bold">{name}</h2>}

      {selectedCount > 1 && (
        <p className="text-[30px] leading-[45px]">
          {totalPackets} packets | {selectedCount} supplements
        </p>
      )}

      <hr className="my-[16px] border-t-[3px] border-black opacity-100" />

      <h3 className="text-[26px] leading-[35px] font-medium">
        Supplement Facts
      </h3>

      <hr className="my-[16px] border-t-[3px] border-black opacity-100" />

      {supplements.map((supplement, index) => (
        <div key={index} className="flex shrink-0 flex-col leading-[35px]">
          <p className="pb-[8px] text-[26px]">
            <span className="font-bold">{supplement.item.name}</span> |{' '}
            {supplement.item.strength} | {supplement.quantity} {supplement.type}
          </p>
          <p className="min-h-[1.5em] text-[23px] italic">
            {getItemDescription(supplement.item.name)}
          </p>
          <hr className="my-[16px] border-t-[3px] border-black opacity-100" />
        </div>
      ))}

      {instruction && (
        <>
          <p className="text-[23px] leading-[35px]">{instruction}</p>
          <hr className="my-3 border-t-[3px] border-black opacity-100" />
        </>
      )}

      {orderLotNumber && (
        <>
          <p className="text-[23px] leading-[35px]">LOT: {orderLotNumber}</p>
          <hr className="my-3 border-t-[3px] border-black opacity-100" />
        </>
      )}

      <p className="text-[20px]">
        Prepared and issued according to the patient&apos;s order. Follows the
        recommendations and advice of a doctor or pharmacist. Complete with food
        supplements and active ingredients available on the market.
      </p>

      <div className="mt-20 flex gap-3">
        <Button
          className="bg-gray-600 px-4 text-base text-white hover:bg-gray-500"
          onClick={onBack}
        >
          Back
        </Button>
      </div>
    </div>
  );
}
